import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Trash2, X, ShieldAlert, Crown, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/shared/api';
import { useCurrentUser } from '@/shared/auth';

type Role = 'admin' | 'maker' | 'checker' | 'viewer';

interface UserAccessRow {
  id: string;
  email: string;
  role: Role;
  display_name: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

interface UserAccessListResponse {
  rows: UserAccessRow[];
  super_admin_email: string | null;
}

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin:   'Full read/write, can invite users and view audit log',
  maker:   'Can propose recon resolutions',
  checker: 'Can approve maker proposals',
  viewer:  'Read-only — can browse jobs and rows but not act',
};

const ROLE_PILL_CLASS: Record<Role, string> = {
  admin:   'pill-accent',
  checker: 'pill-ok',
  maker:   'pill-info',
  viewer:  'pill-neutral',
};

export function UsersPage() {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const [showInvite, setShowInvite] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<UserAccessListResponse>('/api/admin/users').then(r => r.data),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; patch: Partial<Pick<UserAccessRow, 'role' | 'display_name' | 'is_active'>> }) =>
      api.patch(`/api/admin/users/${vars.id}`, vars.patch).then(r => r.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`).then(r => r.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  const rows = data?.rows ?? [];
  const activeCount = rows.filter(r => r.is_active).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-ink-strong inline-flex items-center gap-2">
            <Users className="w-7 h-7" style={{ color: 'var(--accent)' }} />
            Users & access
          </h1>
          <p className="text-sm text-ink-400 mt-1.5 tracking-tight max-w-2xl">
            Only people listed below can sign in. New sign-ins are denied with a 403 if their email isn't on this list. The super-admin email is exempt (always granted access).
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowInvite(true)}>
          <Plus className="w-4 h-4" /> Invite user
        </button>
      </div>

      {/* Super-admin banner */}
      {data?.super_admin_email && (
        <div
          className="card p-4 mb-6 flex items-start gap-3"
          style={{ borderColor: 'var(--accent)' }}
        >
          <Crown className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
          <div className="text-sm">
            <div className="font-semibold text-ink-strong">Super-admin</div>
            <div className="text-ink-400 mt-0.5">
              <span className="font-mono">{data.super_admin_email}</span> always has full access (configured via the <code className="text-xs px-1 rounded" style={{ background: 'var(--bg-input)' }}>SUPER_ADMIN_EMAIL</code> environment variable). This account cannot be revoked from this page.
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <div className="text-sm text-ink-200">
            <span className="font-semibold text-ink-strong">{activeCount}</span> active
            <span className="text-ink-400"> · {rows.length - activeCount} inactive</span>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-ink-400 text-xs uppercase tracking-wider" style={{ background: 'var(--bg-hover)' }}>
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Invited</th>
              <th className="text-right px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-ink-400">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-ink-400">
                  No users invited yet. {me?.actor_role === 'super_admin' && 'Use the button above to invite your first team member.'}
                </td>
              </tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--glass-border)' }} className={r.is_active ? '' : 'opacity-60'}>
                <td className="px-4 py-3 font-mono text-ink-50">{r.email}</td>
                <td className="px-4 py-3 text-ink-200">{r.display_name ?? '—'}</td>
                <td className="px-4 py-3">
                  <select
                    value={r.role}
                    onChange={(e) => update.mutate({ id: r.id, patch: { role: e.target.value as Role } })}
                    disabled={update.isPending || !r.is_active}
                    className="input py-1 px-2 text-xs"
                    style={{ minWidth: '120px' }}
                  >
                    {(['admin', 'checker', 'maker', 'viewer'] as Role[]).map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-ink-400 font-mono">
                  {format(new Date(r.created_at), 'dd MMM yyyy')}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: r.id, patch: { is_active: !r.is_active } })}
                    className={`pill ${r.is_active ? 'pill-ok' : 'pill-neutral'} cursor-pointer`}
                    title={r.is_active ? 'Click to deactivate' : 'Click to reactivate'}
                  >
                    {r.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Remove ${r.email}? They will lose access immediately.`)) {
                        remove.mutate(r.id);
                      }
                    }}
                    className="text-ink-400 hover:text-status-err transition p-1.5 rounded-md"
                    title="Delete user"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-ink-400 mt-4 tracking-tight">
        Role changes and deactivations take effect on the user's next request. They may need to sign out and back in to refresh their session.
      </p>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('maker');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => api.post('/api/admin/users', {
      email: email.trim().toLowerCase(),
      role,
      display_name: displayName.trim() || undefined,
    }).then(r => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (err: { response?: { data?: { detail?: string; message?: string } } }) => {
      setError(err.response?.data?.detail ?? err.response?.data?.message ?? 'Unable to invite. Try again.');
    },
  });

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="glass-raised rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <h2 className="text-lg font-semibold text-ink-strong tracking-tight inline-flex items-center gap-2">
            <Mail className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Invite a user
          </h2>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-strong transition">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-ink-400 mb-1.5">Email</label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="akshay@acc.ltd"
            />
            <p className="text-xs text-ink-400 mt-1.5">
              Must match the Microsoft account they'll sign in with.
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-ink-400 mb-1.5">Display name <span className="lowercase tracking-normal text-ink-600">(optional)</span></label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              placeholder="Akshay Joshi"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-ink-400 mb-1.5">Role</label>
            <div className="space-y-2">
              {(['admin', 'checker', 'maker', 'viewer'] as Role[]).map(r => (
                <label
                  key={r}
                  className={`block p-3 rounded-lg cursor-pointer transition ${role === r ? 'ring-2' : ''}`}
                  style={{
                    background: role === r ? 'var(--accent-soft)' : 'var(--bg-input)',
                    border: `1px solid ${role === r ? 'var(--accent)' : 'var(--glass-border)'}`,
                  }}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r}
                    checked={role === r}
                    onChange={() => setRole(r)}
                    className="sr-only"
                  />
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`pill ${ROLE_PILL_CLASS[r]}`}>{r}</span>
                  </div>
                  <div className="text-xs text-ink-400 mt-1">{ROLE_DESCRIPTIONS[r]}</div>
                </label>
              ))}
            </div>
          </div>
          {error && (
            <div className="flex gap-2 p-3 rounded-lg text-sm"
                 style={{ background: 'color-mix(in srgb, var(--status-err) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)', color: 'var(--status-err)' }}>
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            type="button"
            disabled={!valid || invite.isPending}
            onClick={() => { setError(null); invite.mutate(); }}
            className="btn-primary"
          >
            {invite.isPending ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
